import { create } from 'zustand';
import apiClient from '../services/api';
import { setPendingTopicCheck, getPendingTopicCheck } from '../hooks/useDashboardGlobalVar';

// 定义分页限制常量，从环境变量读取，默认20
const pageLimit = parseInt(process.env.REACT_APP_FETCH_LIMIT || process.env.FETCH_LIMIT || '20', 10);

// 辅助函数：从搜索结果中收集 (kbId, topicId) 对并设置到全局状态
const collectTopicsFromSearchResults = (items) => {
  if (!items || items.length === 0) return;

  const topicMap = {}; // { kbId: Set<topicId> }

  items.forEach(item => {
    // 从 topicMappings 中获取 topic_id 和 knowledge_base_id
    if (item.topicMappings && Array.isArray(item.topicMappings)) {
      item.topicMappings.forEach(mapping => {
        const topicId = mapping.topic_id;
        const kbId = mapping.knowledge_base_id || 'private'; // 如果没有 kb_id，则认为是私有库

        if (topicId) {
          if (!topicMap[kbId]) {
            topicMap[kbId] = new Set();
          }
          topicMap[kbId].add(topicId);
        }
      });
    }
  });

  // 如果有主题需要检查，合并到现有的 pendingTopicCheck
  if (Object.keys(topicMap).length > 0) {
    const existingCheck = getPendingTopicCheck() || {};
    const mergedCheck = { ...existingCheck };

    for (const [kbId, topicIds] of Object.entries(topicMap)) {
      if (!mergedCheck[kbId]) {
        mergedCheck[kbId] = [];
      }
      // 合并并去重
      const existingSet = new Set(mergedCheck[kbId]);
      topicIds.forEach(id => existingSet.add(id));
      mergedCheck[kbId] = Array.from(existingSet);
    }

    console.log('[searchStore] 收集到的主题需要检查:', mergedCheck);
    setPendingTopicCheck(mergedCheck, 'searchStore.collectTopicsFromSearchResults');
  }
};

export const useSearchStore = create((set, get) => ({
  searchResults: [],
  isLoading: false,
  error: null,
  searchHistory: [],
  pagination: {
    total: 0,
    page: 1,
    limit: pageLimit,
    hasMore: false
  },

  // 执行搜索查询
  search: async (query, page = 1, limit = pageLimit, filterType = 'all', topicId = '', kbId = '', reset = true) => {
    console.log('[searchStore.js line 17] 执行搜索查询 search', query, page, limit, filterType, topicId, kbId, 'reset:', reset);

    if (!query.trim()) {
      set({ searchResults: [], isLoading: false });
      return;
    }

    // 检查查询条件是否发生变化
    const currentState = get();
    const queryChanged = !currentState.lastQuery ||
      currentState.lastQuery !== query ||
      currentState.lastFilterType !== filterType ||
      currentState.lastTopicId !== topicId ||
      currentState.lastKbId !== kbId;

    // 如果查询条件发生变化，强制重置
    if (queryChanged) {
      console.log('[searchStore.js] 检测到查询条件变化，强制重置搜索结果');
      reset = true;
      page = 1;
    }

    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams({
        q: query,
        page: page.toString(),
        limit: limit.toString(),
        filterType: filterType
      });

      if (topicId && topicId.trim()) {
        params.append('topicId', topicId.trim());
      }

      if (kbId && kbId.trim()) {
        params.append('kbId', kbId.trim());
      }

      const response = await apiClient.get(`/search?${params.toString()}`);

      // 新增调试日志
      console.log(`[searchStore.js line 26] response.data完整结构:`, response.data);

      // 新增日志输出
      if (response && response.data && Array.isArray(response.data.items)) {
        console.log(`[searchStore.js line 29] /api/search 返回结果条数: ${response.data.items.length}`);
      } else {
        console.log(`[searchStore.js line 31] /api/search 返回结果条数: null`);
      }

      const { items, total } = response.data;

      // 新增调试日志
      console.log(`[searchStore.js line 37] 解构赋值后 items:`, items);
      console.log(`[searchStore.js line 38] 解构赋值后 total:`, total);

      try {
        console.log(`[searchStore.js] 准备调用 set...`);

        // 关键修改：根据reset参数决定是重置还是追加搜索结果
        if (reset || page === 1) {
          // 重置搜索结果（第一页或重新搜索）
          console.log('[searchStore.js] 重置搜索结果，显示第一页');
          set({
            searchResults: items,
            isLoading: false,
            pagination: {
              total,
              page,
              limit,
              hasMore: total > page * limit
            },
            searchHistory: [
              query,
              ...get().searchHistory.filter(item => item !== query).slice(0, 9)
            ],
            // 记录当前查询条件，用于检测变化
            lastQuery: query,
            lastFilterType: filterType,
            lastTopicId: topicId,
            lastKbId: kbId
          });
        } else {
          // 追加搜索结果（加载更多）
          console.log('[searchStore.js] 追加搜索结果到现有列表');
          const currentResults = get().searchResults;

          // 去重逻辑：基于笔记ID去重
          const existingIds = new Set(currentResults.map(item => item.id || item.note_id));
          const newItems = items.filter(item => {
            const itemId = item.id || item.note_id;
            return !existingIds.has(itemId);
          });

          console.log(`[searchStore.js] 去重结果: 原有${currentResults.length}条，新增${items.length}条，去重后${newItems.length}条`);

          if (newItems.length === 0) {
            console.log('[searchStore.js] 没有新的搜索结果，可能是分页参数问题');
            // 如果没有新结果，可能是分页问题，重置分页状态
            set(state => ({
              ...state,
              pagination: {
                ...state.pagination,
                hasMore: false,
                isLoading: false
              }
            }));
            return;
          }

          set({
            searchResults: [...currentResults, ...newItems],
            isLoading: false,
            pagination: {
              total,
              page,
              limit,
              hasMore: total > (currentResults.length + newItems.length)
            },
            searchHistory: [
              query,
              ...get().searchHistory.filter(item => item !== query).slice(0, 9)
            ],
            // 保持查询条件记录
            lastQuery: query,
            lastFilterType: filterType,
            lastTopicId: topicId,
            lastKbId: kbId
          });
        }

        console.log(`[searchStore.js] set 调用完成`);

        // 收集搜索结果中的主题用于自动刷新检查
        collectTopicsFromSearchResults(items);
      } catch (setError) {
        console.error(`[searchStore.js] set 调用失败:`, setError);
        throw setError;
      }

      // 新增调试日志 - 验证set后的状态
      console.log(`[searchStore.js line 53] set后的searchResults:`, get().searchResults);

      // 添加延迟检查，看看是否被意外清空
      setTimeout(() => {
        console.log(`[searchStore.js] 100ms后的searchResults:`, get().searchResults);
      }, 100);

      setTimeout(() => {
        console.log(`[searchStore.js] 500ms后的searchResults:`, get().searchResults);
      }, 500);
    } catch (error) {
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Search failed',
        searchResults: [],
      });
    }
  },

  // 语义搜索
  semanticSearch: async (query, page = 1, limit = pageLimit, filterType = 'all', topicId = '', kbId = '', searchtype = 'hybrid', reset = true) => {
    console.log('[searchStore.js line 54] 执行语义搜索查询 semanticSearch', query, page, limit, filterType, topicId, kbId, searchtype, 'reset:', reset);

    if (!query.trim()) {
      set({ searchResults: [], isLoading: false });
      return;
    }

    // 检查查询条件是否发生变化
    const currentState = get();
    const queryChanged = !currentState.lastQuery ||
      currentState.lastQuery !== query ||
      currentState.lastFilterType !== filterType ||
      currentState.lastTopicId !== topicId ||
      currentState.lastKbId !== kbId ||
      currentState.lastSearchType !== searchtype;

    // 如果查询条件发生变化，强制重置
    if (queryChanged) {
      console.log('[searchStore.js] 检测到语义搜索查询条件变化，强制重置搜索结果');
      reset = true;
      page = 1;
    }

    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams({
        q: query,
        page: page.toString(),
        limit: limit.toString(),
        filterType: filterType,
        searchtype: searchtype
      });

      if (topicId && topicId.trim()) {
        params.append('topicId', topicId.trim());
      }

      if (kbId && kbId.trim()) {
        params.append('kbId', kbId.trim());
      }

      const response = await apiClient.get(`/search/semantic?${params.toString()}`);

      const { items, total } = response.data;

      // 关键修改：根据reset参数决定是重置还是追加搜索结果
      if (reset || page === 1) {
        // 重置搜索结果（第一页或重新搜索）
        console.log('[searchStore.js] 重置语义搜索结果，显示第一页');
        set({
          searchResults: items,
          isLoading: false,
          pagination: {
            total,
            page,
            limit,
            hasMore: total > page * limit
          },
          searchHistory: [
            query,
            ...get().searchHistory.filter(item => item !== query).slice(0, 9)
          ],
          // 记录当前查询条件，用于检测变化
          lastQuery: query,
          lastFilterType: filterType,
          lastTopicId: topicId,
          lastKbId: kbId,
          lastSearchType: searchtype
        });
      } else {
        // 追加搜索结果（加载更多）
        console.log('[searchStore.js] 追加语义搜索结果到现有列表');
        const currentResults = get().searchResults;

        // 去重逻辑：基于笔记ID去重
        const existingIds = new Set(currentResults.map(item => item.id || item.note_id));
        const newItems = items.filter(item => {
          const itemId = item.id || item.note_id;
          return !existingIds.has(itemId);
        });

        console.log(`[searchStore.js] 语义搜索去重结果: 原有${currentResults.length}条，新增${items.length}条，去重后${newItems.length}条`);

        if (newItems.length === 0) {
          console.log('[searchStore.js] 语义搜索没有新的搜索结果，可能是分页参数问题或所有记录都被过滤');
          // 即使返回0条记录，也要将 page 加1，以便下次加载下一页
          // 判断是否还有更多数据：如果 total > 当前已加载的数据量，说明可能还有更多数据
          const hasMore = total > currentResults.length;
          console.log(`[searchStore.js] 返回0条记录，更新分页: page=${page}, hasMore=${hasMore}, total=${total}, currentResults.length=${currentResults.length}`);
          set(state => ({
            ...state,
            isLoading: false,
            pagination: {
              ...state.pagination,
              total,
              page, // 更新为当前页（已经加1了）
              limit,
              hasMore: hasMore, // 根据 total 和当前已加载数据量判断是否还有更多
              isLoading: false
            }
          }));
          return;
        }

        set({
          searchResults: [...currentResults, ...newItems],
          isLoading: false,
          pagination: {
            total,
            page,
            limit,
            hasMore: total > (currentResults.length + newItems.length),
            isLoading: false // 确保 isLoading 被设置为 false
          },
          searchHistory: [
            query,
            ...get().searchHistory.filter(item => item !== query).slice(0, 9)
          ],
          // 保持查询条件记录
          lastQuery: query,
          lastFilterType: filterType,
          lastTopicId: topicId,
          lastKbId: kbId,
          lastSearchType: searchtype
        });
      }

      // 收集搜索结果中的主题用于自动刷新检查
      collectTopicsFromSearchResults(items);
    } catch (error) {
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Semantic search failed',
        searchResults: [],
      });
    }
  },

  // 加载更多搜索结果
  loadMoreSearchResults: async (query, filterType = 'all', topicId = '', kbId = '', searchtype = 'hybrid') => {
    const { pagination, isLoading } = get();

    if (isLoading || pagination.isLoading) {
      console.log('[loadMoreSearchResults] 正在加载中，跳过重复请求');
      return;
    }

    const nextPage = pagination.page + 1;
    const totalPages = Math.ceil(pagination.total / pagination.limit);

    console.log('[loadMoreSearchResults] 分页信息:', { nextPage, totalPages, current: pagination.page });

    if (nextPage > totalPages) {
      console.log('[loadMoreSearchResults] 已经加载完所有搜索结果');
      return;
    }

    // 设置分页加载状态
    set(state => ({
      ...state,
      pagination: {
        ...state.pagination,
        isLoading: true
      }
    }));

    console.log(`[loadMoreSearchResults] 开始加载第${nextPage}页搜索结果...`);
    try {
      // 根据搜索类型选择不同的搜索方法
      if (searchtype === 'fulltext') {
        await get().search(query, nextPage, pagination.limit, filterType, topicId, kbId, false);
      } else {
        await get().semanticSearch(query, nextPage, pagination.limit, filterType, topicId, kbId, searchtype, false);
      }
    } catch (error) {
      console.error('[loadMoreSearchResults] 加载失败:', error);
      // 重置分页加载状态
      set(state => ({
        ...state,
        pagination: {
          ...state.pagination,
          isLoading: false
        }
      }));
    }
  },

  // 清除搜索历史
  clearSearchHistory: () => {
    console.log('[searchStore.js line 90] 清除搜索历史 clearSearchHistory');
    set({ searchHistory: [] });
  },

  // 清除搜索结果
  clearSearchResults: () => {
    console.log('[searchStore.js line 97] 清除搜索结果 clearSearchResults');
    console.log('[searchStore.js] clearSearchResults 调用堆栈:', new Error().stack);
    set({
      searchResults: [],
      pagination: {
        total: 0,
        page: 1,
        limit: pageLimit,
        hasMore: false
      },
      // 清理所有查询条件记录
      lastQuery: null,
      lastFilterType: null,
      lastTopicId: null,
      lastKbId: null,
      lastSearchType: null,
      isLoading: false,
      error: null
    });
  },

  // 去重搜索结果
  deduplicateSearchResults: () => {
    const state = get();
    const { searchResults } = state;

    if (searchResults.length === 0) return;

    // 基于笔记ID去重
    const uniqueResults = [];
    const seenIds = new Set();

    searchResults.forEach(item => {
      const itemId = item.id || item.note_id;
      if (itemId && !seenIds.has(itemId)) {
        seenIds.add(itemId);
        uniqueResults.push(item);
      }
    });

    if (uniqueResults.length !== searchResults.length) {
      console.log(`[searchStore] 去重完成: 原有${searchResults.length}条，去重后${uniqueResults.length}条`);
      set({
        searchResults: uniqueResults,
        pagination: {
          ...state.pagination,
          total: Math.max(state.pagination.total, uniqueResults.length),
          hasMore: state.pagination.total > uniqueResults.length
        }
      });
    }
  },

  // 强制重置搜索状态（用于查询条件变化时）
  forceResetSearch: () => {
    console.log('[searchStore] 强制重置搜索状态');
    set({
      searchResults: [],
      pagination: {
        total: 0,
        page: 1,
        limit: pageLimit,
        hasMore: false
      },
      lastQuery: null,
      lastFilterType: null,
      lastTopicId: null,
      lastKbId: null,
      lastSearchType: null,
      isLoading: false,
      error: null
    });
  },

  // 强制清理所有状态（用于类型切换时）
  forceCleanAll: () => {
    console.log('[searchStore] 强制清理所有状态');
    set({
      searchResults: [],
      pagination: {
        total: 0,
        page: 1,
        limit: pageLimit,
        hasMore: false
      },
      lastQuery: null,
      lastFilterType: null,
      lastTopicId: null,
      lastKbId: null,
      lastSearchType: null,
      isLoading: false,
      error: null,
      searchHistory: []
    });
  },

  // 检查查询条件是否发生变化
  hasQueryChanged: (query, filterType, topicId, kbId, searchtype = null) => {
    const state = get();
    const queryChanged = !state.lastQuery ||
      state.lastQuery !== query ||
      state.lastFilterType !== filterType ||
      state.lastTopicId !== topicId ||
      state.lastKbId !== kbId ||
      (searchtype && state.lastSearchType !== searchtype);

    console.log('[searchStore] 查询条件变化检测:', {
      current: {
        lastQuery: state.lastQuery,
        lastFilterType: state.lastFilterType,
        lastTopicId: state.lastTopicId,
        lastKbId: state.lastKbId,
        lastSearchType: state.lastSearchType
      },
      new: {
        query,
        filterType,
        topicId,
        kbId,
        searchtype
      },
      changed: queryChanged
    });

    return queryChanged;
  },

  // 调试方法：获取当前状态
  getDebugInfo: () => {
    const state = get();
    console.log('[searchStore] 当前状态:', {
      searchResultsLength: state.searchResults.length,
      pagination: state.pagination,
      isLoading: state.isLoading,
      error: state.error
    });
    return state;
  },
})); 